// Section (f): what you want near the house.
//
// Distinct from "what the house needs" on purpose. A dining table that seats
// everyone is a property of the building; a lake is a property of where the
// building is. They are decided at different stages — houses vs regions — so
// keeping them apart makes both easier to act on.
//
// PROSE, NOT CHIPS. This shipped as eleven chips with a fixed vocabulary. The
// argument for structure was real — `region.water_type` is already
// `bay | ocean | lake` — but a fixed list answers the question whoever wrote the
// list thought of, not the one the family has. "Somewhere the kids can ride
// bikes without a road" was never going to be one of eleven options.
//
// Same must-have / nice-to-have shape as the house requirements above it, so the
// form asks its two questions the same way rather than in two idioms.
import { useEffect, useState, type JSX } from 'react'

export interface LocationFeaturesFieldProps {
  /** Persisted household.location_must_have_raw. */
  mustHave: string | null
  /** Persisted household.location_nice_to_have_raw. */
  niceToHave: string | null
  disabled?: boolean
  onMustHaveChange: (value: string) => void
  onNiceToHaveChange: (value: string) => void
}

export function LocationFeaturesField({
  mustHave,
  niceToHave,
  disabled = false,
  onMustHaveChange,
  onNiceToHaveChange,
}: LocationFeaturesFieldProps): JSX.Element {
  const [mustDraft, setMustDraft] = useState(mustHave ?? '')
  const [niceDraft, setNiceDraft] = useState(niceToHave ?? '')

  // Re-sync when the row reloads or an organizer switches household — not on
  // every keystroke, since the saved value lags the draft by design.
  useEffect(() => {
    setMustDraft(mustHave ?? '')
  }, [mustHave])

  useEffect(() => {
    setNiceDraft(niceToHave ?? '')
  }, [niceToHave])

  return (
    <div>
      <p className="spz-hint">
        Not the house itself &mdash; what you&rsquo;d like within reach of it. A lake, hiking,
        somewhere to climb, a town you can walk to.
      </p>

      <label className="spz-field">
        <span className="spz-label">Must have</span>
        <textarea
          className="spz-textarea"
          rows={3}
          placeholder="e.g. on water we can swim in, trails from the door…"
          value={mustDraft}
          disabled={disabled}
          onChange={(e) => {
            setMustDraft(e.target.value)
            onMustHaveChange(e.target.value)
          }}
          onBlur={() => onMustHaveChange(mustDraft)}
        />
      </label>
      <p className="spz-hint">
        Nothing here rules a place out on its own &mdash; it tells the organizers what matters.
      </p>

      <label className="spz-field" style={{ marginTop: '0.9rem' }}>
        <span className="spz-label">
          Nice to have <span className="spz-hint">(optional)</span>
        </span>
        <textarea
          className="spz-textarea"
          rows={3}
          placeholder="e.g. a golf course, a decent bakery, somewhere to launch a kayak…"
          value={niceDraft}
          disabled={disabled}
          onChange={(e) => {
            setNiceDraft(e.target.value)
            onNiceToHaveChange(e.target.value)
          }}
          onBlur={() => onNiceToHaveChange(niceDraft)}
        />
      </label>
    </div>
  )
}

export default LocationFeaturesField
